import { sb } from '../supabase';
import { SUPPLIER_REVIEW_QUEUE_RAW_STATUSES } from './supplierOnboarding';

// Data layer for the Admin Console "Suppliers" section — a UNIFIED, deduped list
// of every registered supplier plus the unclaimed directory factories:
//   • profiles (role='supplier')            → registered suppliers (real status).
//        origin 'factory_claim' if a factory_directory row links to them,
//        otherwise origin 'organic'.
//   • factory_directory (linked_supplier_id IS NULL) → unclaimed directory
//        factories, origin 'directory' (a prepared page, not yet a real account).
// A CLAIMED factory (is_active=false, linked_supplier_id set) now yields BOTH a
// factory row and its supplier profile row. It used to yield only the profile,
// which made a factory vanish from the catalog list the moment its owner claimed
// it — you could not answer "is this factory claimed?" from the list that was
// supposed to be the catalog. The two rows never collide on screen because the
// console shows them in different tabs: factory rows in "our factories",
// profile rows in "registered". Consumers that want the old behaviour filter on
// `claimed === false`.
// Admins read both base tables directly under is_admin_user() RLS.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const newest = (arr) => arr.filter(Boolean).sort().pop() || null;

export async function fetchConsoleSuppliers() {
  const [
    { data: facs, error: facErr },
    { data: prods, error: prodErr },
    { data: imps, error: impErr },
    { data: sups, error: supErr },
    { data: sprods, error: sprodErr },
  ] = await Promise.all([
    sb.from('factory_directory').select(
      'id, company_name, company_name_latin, category, city, country, address, profile_image, ' +
      'factory_images, certifications, is_verified, is_featured, is_active, private_label, ' +
      'linked_supplier_id, claimed_at, email, phone, founded_year, export_markets, description_ar, description_en, ' +
      'created_at, updated_at, updated_by',
    ),
    sb.from('factory_products').select('factory_id, created_at, owner_confirmed_at'),
    sb.from('factory_catalog_imports').select('factory_id, created_at'),
    sb.from('profiles').select(
      'id, full_name, company_name, company_name_latin, country, city, speciality, status, ' +
      'maabar_supplier_id, avatar_url, phone, whatsapp, email, created_at',
    ).eq('role', 'supplier'),
    sb.from('products').select('supplier_id, created_at'),
  ]);
  if (facErr) throw facErr;
  if (supErr) throw supErr;
  // These three only feed counts + last activity, so a failure must not blank the
  // screen the way facErr/supErr rightly do — but it must not vanish either. This
  // file selected a non-existent `updated_at` on both product tables; the 400s were
  // dropped on the floor, and every row silently read "0 products".
  if (prodErr)  console.error('[consoleSuppliers] factory_products query failed:', prodErr.message || prodErr);
  if (impErr)   console.error('[consoleSuppliers] factory_catalog_imports query failed:', impErr.message || impErr);
  if (sprodErr) console.error('[consoleSuppliers] products query failed:', sprodErr.message || sprodErr);

  // Resolve the last editor's display name for each factory (updated_by → profile).
  // Editors are staff/admins, not in the supplier `sups` set, so fetch separately.
  const editorIds = [...new Set((facs || []).map((f) => f.updated_by).filter(Boolean))];
  const editorMap = {};
  if (editorIds.length) {
    const { data: eds } = await sb.from('profiles').select('id, full_name, email').in('id', editorIds);
    for (const e of eds || []) editorMap[e.id] = nf(e.full_name) || nf(e.email) || null;
  }
  const editorName = (id) => (id ? editorMap[id] || null : null);

  // factory_products / catalog imports → per-factory counts + last activity
  const pCount = {}, pLast = {};
  for (const p of prods || []) {
    const f = p.factory_id; if (!f) continue;
    pCount[f] = (pCount[f] || 0) + 1;
    // No updated_at on factory_products — owner_confirmed_at is the only real "touched"
    // signal besides creation.
    const at = newest([p.created_at, p.owner_confirmed_at]);
    if (at && (!pLast[f] || at > pLast[f])) pLast[f] = at;
  }
  const cCount = {}, cLast = {};
  for (const im of imps || []) {
    const f = im.factory_id; if (!f) continue;
    cCount[f] = (cCount[f] || 0) + 1;
    if (im.created_at && (!cLast[f] || im.created_at > cLast[f])) cLast[f] = im.created_at;
  }

  // legacy products (supplier_id) → per-supplier counts + last activity
  const spCount = {}, spLast = {};
  for (const p of sprods || []) {
    const s = p.supplier_id; if (!s) continue;
    spCount[s] = (spCount[s] || 0) + 1;
    if (p.created_at && (!spLast[s] || p.created_at > spLast[s])) spLast[s] = p.created_at;
  }

  // profileId → its (claimed, deactivated) factory row, so a claim keeps a link
  // to the archival factory detail and we can skip that factory row below.
  const factoryByOwner = {};
  for (const f of facs || []) { if (f.linked_supplier_id) factoryByOwner[f.linked_supplier_id] = f; }

  // (a) every supplier profile → one unified row
  const profileRows = (sups || []).map((p) => {
    const fac = factoryByOwner[p.id] || null;
    const statusL = String(p.status || '').trim().toLowerCase();
    // A supplier awaiting a verification decision belongs on the verification
    // screen, whatever their origin. Without this, an organic supplier lands in
    // the legacy /admin console and a claimed one lands on the archival factory
    // tab — which has no verification file at all.
    const awaitingReview = SUPPLIER_REVIEW_QUEUE_RAW_STATUSES.includes(statusL);
    return {
      id: p.id,
      kind: 'profile',
      // When the ACCOUNT was created. Distinct from last_activity, which a long
      // dormant supplier refreshes the moment they log in — using it to answer
      // "who signed up recently" would keep reporting old accounts as new.
      created_at: p.created_at,
      origin: fac ? 'factory_claim' : 'organic',
      // awaiting review → the verification file; otherwise claimed → the
      // archival factory detail, organic → the verification file too. It opens
      // every supplier status in production (all 67 bucket on 2026-10-11), and
      // the owner's rule is that the new console never lands in /admin.
      openPath: awaitingReview || !fac
        ? `/admin2/verification/${p.id}`
        : `/admin2/suppliers/${fac.id}`,
      company_name: nf(p.company_name) || nf(p.full_name),
      company_name_latin: nf(p.company_name_latin),
      category: fac ? fac.category : nf(p.speciality),
      city: nf(p.city),
      country: nf(p.country),
      profile_image: p.avatar_url || fac?.profile_image || null,
      status: p.status || null,
      maabar_supplier_id: nf(p.maabar_supplier_id),
      is_verified: statusL === 'verified',
      is_registered: true,
      is_active: statusL !== 'rejected' && statusL !== 'inactive',
      is_featured: !!fac?.is_featured,
      email: nf(p.email) || nf(fac?.email),
      phone: nf(p.phone) || nf(p.whatsapp) || nf(fac?.phone),
      product_count: spCount[p.id] || 0,
      catalog_count: fac ? (cCount[fac.id] || 0) : 0,
      // Who put the content there. A claimed factory usually carries products we
      // imported AND products its owner has since uploaded, and telling those
      // apart is how you know whether a supplier has actually shown up.
      maabar_product_count: fac ? (pCount[fac.id] || 0) : 0,
      has_products: (spCount[p.id] || 0) > 0,
      has_catalog: fac ? (cCount[fac.id] || 0) > 0 : false,
      has_profile: true,
      last_activity: newest([p.created_at, spLast[p.id], fac?.updated_at, fac ? pLast[fac.id] : null]),
      last_editor: fac ? editorName(fac.updated_by) : null,
    };
  });

  // (b) EVERY directory factory → a factory row, claimed or not. The owner's
  // name comes from the profile the claim points at, so the list can say who
  // claimed it without a second query.
  const supById = {};
  for (const p of sups || []) supById[p.id] = p;

  const factoryRows = (facs || []).map((f) => {
    const owner = f.linked_supplier_id ? supById[f.linked_supplier_id] || null : null;
    const hasProfile = !!(f.profile_image || nf(f.description_ar) || nf(f.description_en)
      || (Array.isArray(f.factory_images) && f.factory_images.length > 0));
    return {
      ...f,
      kind: 'factory',
      // `origin` stays 'directory' for every factory row: it records where the
      // ROW came from (we typed it into the catalog), which a claim does not
      // change. Whether it has since been claimed is `claimed` — a state, not
      // an origin. Collapsing the two is what made a claimed factory look like
      // a different kind of thing and drop out of the catalog entirely.
      origin: 'directory',
      claimed: !!f.linked_supplier_id,
      claimed_at: f.claimed_at || null,
      claimed_by: f.linked_supplier_id || null,
      claimed_by_name: owner ? (nf(owner.company_name) || nf(owner.full_name) || null) : null,
      openPath: `/admin2/suppliers/${f.id}`,
      // A claimed factory is set is_active=false BY THE CLAIM, not by an editor
      // parking it. Calling that 'draft' reads as "unfinished", which is wrong
      // and is the one label an admin would act on by mistake.
      status: f.linked_supplier_id ? 'claimed' : f.is_active ? 'listed' : 'draft',
      product_count: pCount[f.id] || 0,
      maabar_product_count: pCount[f.id] || 0,
      catalog_count: cCount[f.id] || 0,
      is_registered: false,
      has_products: (pCount[f.id] || 0) > 0,
      has_catalog: (cCount[f.id] || 0) > 0,
      has_profile: hasProfile,
      last_activity: newest([f.updated_at, pLast[f.id], cLast[f.id], f.created_at]),
      last_editor: editorName(f.updated_by),
    };
  });

  return [...profileRows, ...factoryRows];
}

// Distinct non-empty countries / categories present (for the filter dropdowns).
export function deriveFacets(rows) {
  const countries = new Set(), categories = new Set();
  for (const r of rows) {
    if (nf(r.country)) countries.add(r.country);
    if (nf(r.category)) categories.add(r.category);
  }
  return {
    countries: Array.from(countries).sort(),
    categories: Array.from(categories).sort(),
  };
}
